'use strict'

/**
 * Name: Day18 - Particle
 * ===========================================
 */

import * as PIXI from 'pixi.js'
import { rand, randArray } from '@/helpers/helpers'

const COLOR = '0xEEEEEE'
const COLORS = [0x32E0FE, 0xD761E9, 0x1379CB, 0x5C6CDE, 0x31EEDA]

/**
 * Particle Class
 *
 * @constructor {DomElement} el
 * @constructor {Object} options
 */
export class Particle {
  constructor(el, options = {}) {
    this.parentEl = el
    this.points = []
    this.app = false
    this.ratio = false
    this.container = false
    this.containerChild = false
    this.sprites = []

    window.addEventListener('debounceResize', this.resize.bind(this))

    this.init()
  }

  init() {
    const { width, height } = this.parentEl.getBoundingClientRect()
    this.app = new PIXI.Application(width, height, {
      transparent: true,
      resolution: 2,
      autoResize: true,
      antialias: true
    })
    this.ratio = width / height
    this.parentEl.appendChild(this.app.view)

    this.w = this.app.view.width / 2
    this.h = this.app.view.height / 2

    this.count = this.w / 60
    this.delta = 0.25

    this.container = new PIXI.particles.ParticleContainer(100, {
      scale: false,
      position: true,
      rotation: true,
      uvs: false,
      alpha: true
    })
    this.containerChild = new PIXI.particles.ParticleContainer(100, {
      scale: false,
      position: true,
      rotation: true,
      uvs: false,
      alpha: true
    })
    this.containerNormal = new PIXI.particles.ParticleContainer(100, {
      scale: false,
      position: true,
      rotation: true,
      uvs: false,
      alpha: true
    })
    this.app.stage.addChild(this.container)
    this.app.stage.addChild(this.containerNormal)
    this.app.stage.addChild(this.containerChild)

    const graphics = new PIXI.Graphics()
    graphics.beginFill(COLOR)
    graphics.drawCircle(0, 0, 100)
    graphics.endFill()
    graphics.setTransform(0, 0, 0.9, 0.9)
    graphics.alpha = 0.7
    graphics._filters = [new PIXI.filters.BlurFilter(2)]
    this.textureBlurLevelLow = this.app.renderer.generateTexture(graphics)

    const graphicsLevelNormal = new PIXI.Graphics()
    graphicsLevelNormal.beginFill(COLOR)
    graphicsLevelNormal.drawCircle(0, 0, 100)
    graphicsLevelNormal.endFill()
    graphicsLevelNormal.setTransform(0, 0, 0.9, 0.9)
    graphicsLevelNormal._filters = [new PIXI.filters.BlurFilter(6)]
    this.textureBlurLevelNormal = this.app.renderer.generateTexture(graphicsLevelNormal)

    const graphicsLevelLarge = new PIXI.Graphics()
    graphicsLevelLarge.beginFill(COLOR)
    graphicsLevelLarge.drawCircle(0, 0, 100)
    graphicsLevelLarge.endFill()
    graphicsLevelLarge.setTransform(0, 0, 0.8, 0.8)
    graphicsLevelLarge._filters = [new PIXI.filters.BlurFilter(12)]
    this.textureBlurLevelHigh = this.app.renderer.generateTexture(graphicsLevelLarge)

    for (let i = 0; i < this.count; i++) {
      this.initPoint()
    }

    for (let i = 0; i < this.points.length; i++) {
      const point = this.points[i]
      const texture = (point.blur === 'low') ? this.textureBlurLevelHigh : (point.blur === 'normal') ? this.textureBlurLevelNormal : this.textureBlurLevelLow
      const s = new PIXI.Sprite(texture)
      s.width = this.points[i].size
      s.height = this.points[i].size
      s.position.x = this.points[i].x
      s.position.y = this.points[i].y
      s.anchor.x = 0.5
      s.anchor.y = 0.5
      s.alpha = this.points[i].alpha
      s.tint = randArray(COLORS)
      this.sprites.push(s)

      if (point.blur === 'high') {
        this.container.addChild(s)
      } else if (point.blur === 'normal') {
        this.containerNormal.addChild(s)
      } else {
        this.containerChild.addChild(s)
      }
    }

    // this.container.scale.set(1)
    this.container.x = this.app.screen.width / 2
    this.container.y = this.app.screen.height / 2
    this.container.pivot.x = this.app.screen.width / 2
    this.container.pivot.y = this.app.screen.height / 2

    // this.containerNormal.scale.set(1.2)
    this.containerNormal.x = this.app.screen.width / 2
    this.containerNormal.y = this.app.screen.height / 2
    this.containerNormal.pivot.x = this.app.screen.width / 2
    this.containerNormal.pivot.y = this.app.screen.height / 2

    // this.containerChild.scale.set(1.5)
    this.containerChild.x = this.app.screen.width / 2
    this.containerChild.y = this.app.screen.height / 2
    this.containerChild.pivot.x = this.app.screen.width / 2
    this.containerChild.pivot.y = this.app.screen.height / 2

    // Listen for animate update
    this.app.ticker.add(delta => {
      this.container.rotation += 0.0005
      this.containerNormal.rotation += 0.00025
      this.containerChild.rotation -= 0.0005
      this.updatePosition()
    }, PIXI.UPDATE_PRIORITY.LOW)
  }

  /**
   * ポイントの初期化
   */
  initPoint() {
    const x = this.w * Math.random()
    const y = this.h * Math.random()
    const dx = -this.delta / 2 + this.delta * Math.random()
    const dy = 0 // -this.delta / 10 + this.delta * Math.random()

    let sizeLabel = rand(1, 4)

    const nowLargeSize = this.points.filter(point => point.level >= 3)
    if (nowLargeSize.length > this.count / 4) {
      sizeLabel = rand(1, 2)
    }

    let size = rand(70, 90)
    size = (sizeLabel === 4) ? size * 4 : (sizeLabel === 3) ? size * 3.5 : (sizeLabel === 2) ? size * 2 : size

    const blur = (sizeLabel === 1) ? 'low' : (sizeLabel === 2) ? 'normal' : 'high'
    const alpha = rand(0, 2) * 0.1

    this.points.push({
      level: sizeLabel,
      isScaleUp: true,
      SCALE_CHANGE_NUMBER: rand(1, 10) * 0.0001,
      SCALE_UP_LIMIT: 0.25,
      SCALE_DOWN_LIMIT: 0,
      x: x,
      y: y,
      dx: dx,
      dy: dy,
      size: size,
      blur: blur,
      alpha: alpha
    })
  }

  /**
   * ポイントの描画
   */
  drawPoint(point, i) {
    this.sprites[i].position.x = point.x
    this.sprites[i].position.y = point.y
    this.sprites[i].alpha = this.points[i].alpha
  }

  /**
   * ポイントの位置変更
   */
  updatePosition() {
    for (let i = 0; i < this.points.length; i++) {
      // for Position Update
      let xDx = this.points[i].x + this.points[i].dx
      let yDy = this.points[i].y + this.points[i].dy
      if (xDx - this.points[i].size < 0 || xDx + this.points[i].size > this.w) {
        this.points[i].dx = -this.points[i].dx
      }
      if (yDy - this.points[i].size < 0 || yDy + this.points[i].size > this.h) {
        this.points[i].dy = -this.points[i].dy
      }
      this.points[i].x += this.points[i].dx
      this.points[i].y += this.points[i].dy

      // for Alpha Update
      if (this.points[i].isScaleUp) {
        this.points[i].alpha += this.points[i].SCALE_CHANGE_NUMBER
        if (this.points[i].alpha > this.points[i].SCALE_UP_LIMIT) {
          this.points[i].alpha = this.points[i].SCALE_UP_LIMIT - this.points[i].SCALE_CHANGE_NUMBER
          this.points[i].isScaleUp = false
        }
      } else {
        this.points[i].alpha -= this.points[i].SCALE_CHANGE_NUMBER
        if (this.points[i].alpha < this.points[i].SCALE_DOWN_LIMIT) {
          this.points[i].x = this.w * Math.random()
          this.points[i].y = this.h * Math.random()
          this.points[i].alpha = this.points[i].SCALE_DOWN_LIMIT + this.points[i].SCALE_CHANGE_NUMBER
          this.points[i].isScaleUp = true
        }
      }

      this.drawPoint(this.points[i], i)
    }
  }

  resize() {
    const { width, height } = this.parentEl.getBoundingClientRect()
    let w
    let h
    if (width / height >= this.ratio) {
      w = height * this.ratio
      h = height
    } else {
      w = width
      h = width / this.ratio
    }
    this.app.view.style.width = `${w}px`
    this.app.view.style.height = `${h}px`
  }

  destroy() {
    this.app.destroy(true)
    this.app = null
  }
}
